import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader, Play } from "lucide-react";
import { toast } from "sonner";
import {
  PriceModel,
  CreatePriceModelParams,
  UpdatePriceModelParams,
  ExecutePriceModelResponse,
  extractSchemaDefaults,
  isInputRecord,
  validatePriceModelInputSchema,
} from "@forgepoint/shared";
import { usePriceModelsStore } from "./priceModelsStore";
import { cn } from "@/lib/utils";

interface PriceModelFormData {
  name: string;
  description: string;
  script: string;
  inputSchema: string;
  defaultInputs: string;
}

/**
 * Builds the effective defaults for test input pre-population by merging
 * schema-level defaults with the explicit defaultInputs column.
 * @param pm - The price model
 * @returns Merged defaults object (schema defaults as base, defaultInputs overrides)
 */
function getEffectiveDefaults(pm: PriceModel): Record<string, unknown> {
  const schemaDefaults = extractSchemaDefaults(pm.inputSchema);
  return { ...schemaDefaults, ...pm.defaultInputs };
}

const getInitialFormData = (): PriceModelFormData => ({
  name: "",
  description: "",
  script: "",
  inputSchema: JSON.stringify({ type: "object", properties: {} }, null, 2),
  defaultInputs: JSON.stringify({}, null, 2),
});

const getFormDataFromPriceModel = (pm: PriceModel): PriceModelFormData => ({
  name: pm.name,
  description: pm.description || "",
  script: pm.script,
  inputSchema: JSON.stringify(pm.inputSchema, null, 2),
  defaultInputs: JSON.stringify(pm.defaultInputs, null, 2),
});

/**
 * Validates the input schema text the same way the server does, so a schema the
 * item editor could not render is rejected before it is saved.
 * @param inputSchema - The raw input schema JSON from the form
 * @returns The error to show, or null when the schema is usable
 */
function getInputSchemaError(inputSchema: string): string | null {
  const parsed = parseJson(inputSchema);
  if (!isInputRecord(parsed)) {
    return "Input schema must be a valid JSON object.";
  }

  return validatePriceModelInputSchema(parsed);
}

/**
 * Parses JSON without throwing, so malformed text is handled as a value.
 * @param raw - The raw JSON text
 * @returns The parsed value, or undefined when the text is not valid JSON
 */
function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

type PriceModelEditorSheetProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      mode: "create";
      priceModelId?: undefined;
      onSaved?: () => void;
    }
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      mode: "edit";
      priceModelId: string;
      onSaved?: () => void;
    };

export function PriceModelEditorSheet({
  open,
  onOpenChange,
  mode,
  priceModelId,
  onSaved,
}: PriceModelEditorSheetProps) {
  const {
    getPriceModel,
    fetchPriceModel,
    createPriceModel,
    updatePriceModel,
    executePriceModel,
    getPriceModelLoadingState,
  } = usePriceModelsStore();

  const priceModel =
    mode === "edit" && priceModelId ? getPriceModel(priceModelId) : undefined;
  const loadingState =
    mode === "edit" && priceModelId
      ? getPriceModelLoadingState(priceModelId)
      : null;

  const [formData, setFormData] =
    useState<PriceModelFormData>(getInitialFormData());
  const [isSaving, setIsSaving] = useState(false);
  const [testInputs, setTestInputs] = useState("");
  const [testQuantities, setTestQuantities] = useState("1, 10, 100");
  const [testResult, setTestResult] =
    useState<ExecutePriceModelResponse | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [inputSchemaError, setInputSchemaError] = useState<string | null>(null);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === "edit" && priceModelId) {
      fetchPriceModel(priceModelId);
    } else {
      setFormData(getInitialFormData());
      setTestResult(null);
      setExecuteError(null);
      setInputSchemaError(null);
      setTestInputs("");
    }
  }, [open, mode, priceModelId, fetchPriceModel]);

  useEffect(() => {
    if (open && mode === "edit" && priceModel && !loadingState?.isLoadingGet) {
      setFormData(getFormDataFromPriceModel(priceModel));
      setTestInputs(JSON.stringify(getEffectiveDefaults(priceModel), null, 2));
      setTestResult(null);
      setExecuteError(null);
      setInputSchemaError(null);
    }
  }, [open, mode, priceModel, loadingState?.isLoadingGet]);

  /**
   * Validates form data before saving. Shows confirmation dialog for edit mode.
   */
  const handleSave = () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (!formData.script.trim()) {
      toast.error("Script is required");
      return;
    }

    const schemaError = getInputSchemaError(formData.inputSchema);
    if (schemaError) {
      setInputSchemaError(schemaError);
      return;
    }
    setInputSchemaError(null);

    if (!isInputRecord(parseJson(formData.defaultInputs))) {
      toast.error("Default inputs must be a valid JSON object");
      return;
    }

    if (mode === "edit") {
      setShowUpdateConfirm(true);
    } else {
      performSave();
    }
  };

  /**
   * Performs the actual save/create operation after validation (and confirmation for edits).
   */
  const performSave = async () => {
    const parsedInputSchema: Record<string, unknown> = JSON.parse(
      formData.inputSchema,
    );
    const parsedDefaultInputs: Record<string, unknown> = JSON.parse(
      formData.defaultInputs,
    );

    setIsSaving(true);
    try {
      if (mode === "create") {
        const params: CreatePriceModelParams = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          script: formData.script,
          inputSchema: parsedInputSchema,
          defaultInputs: parsedDefaultInputs,
        };
        const { priceModel: created, error: createError } =
          await createPriceModel(params);
        if (created) {
          toast.success("Price model created");
          onSaved?.();
          onOpenChange(false);
        } else {
          toast.error(createError ?? "Failed to create price model");
        }
      } else if (priceModelId) {
        const params: UpdatePriceModelParams = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          script: formData.script,
          inputSchema: parsedInputSchema,
          defaultInputs: parsedDefaultInputs,
        };
        const { priceModel: updated, error: updateError } =
          await updatePriceModel(priceModelId, params);
        if (updated) {
          toast.success("Price model updated");
          onSaved?.();
          onOpenChange(false);
        } else {
          toast.error(updateError ?? "Failed to update price model");
        }
      }
    } catch {
      toast.error("Failed to save price model");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestExecute = async () => {
    const targetId = mode === "edit" ? priceModelId : null;
    if (!targetId) {
      toast.error("Save the price model first before testing");
      return;
    }

    let parsedInputs: Record<string, unknown>;
    try {
      parsedInputs = JSON.parse(testInputs || "{}");
    } catch {
      toast.error("Test inputs must be valid JSON");
      return;
    }

    let parsedQuantities: number[];
    try {
      parsedQuantities = testQuantities
        .split(",")
        .map((q) => parseInt(q.trim(), 10))
        .filter((q) => !isNaN(q) && q > 0);
    } catch {
      toast.error("Quantities must be comma-separated numbers");
      return;
    }

    if (parsedQuantities.length === 0) {
      toast.error("At least one quantity is required");
      return;
    }

    setIsExecuting(true);
    setTestResult(null);
    setExecuteError(null);

    const result = await executePriceModel(targetId, {
      inputs: parsedInputs,
      quantities: parsedQuantities,
    });

    setIsExecuting(false);

    if (result) {
      setTestResult(result);
    } else {
      const state = getPriceModelLoadingState(targetId);
      setExecuteError(state.executeError || "Execution failed");
    }
  };

  const isLoading = loadingState?.isLoadingGet ?? false;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>
            {mode === "create" ? "Create Price Model" : "Edit Price Model"}
          </SheetTitle>
          <SheetDescription>
            {mode === "create"
              ? "Define a Python script that computes item operations, components, and margins from input variables."
              : `Editing ${priceModel?.name || "price model"} (v${priceModel?.version || 1})`}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pm-name">Name *</Label>
                <Input
                  id="pm-name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Aluminum Casting"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-description">Description</Label>
                <Input
                  id="pm-description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  placeholder="What this price model calculates"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pm-script">Python Script *</Label>
              <Textarea
                id="pm-script"
                value={formData.script}
                onChange={(e) =>
                  setFormData({ ...formData, script: e.target.value })
                }
                placeholder="import json, sys&#10;inputs = json.load(sys.stdin)&#10;# ... compute operations, components, margins&#10;print(json.dumps(output))"
                className="font-mono text-sm min-h-[300px]"
                rows={15}
              />
              <p className="text-xs text-muted-foreground">
                Script reads JSON from stdin and prints JSON to stdout. Output
                must include operations array with operationName, unitCost, and
                reasoning fields.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pm-input-schema">Input Schema (JSON)</Label>
              <Textarea
                id="pm-input-schema"
                value={formData.inputSchema}
                onChange={(e) => {
                  setInputSchemaError(null);
                  setFormData({
                    ...formData,
                    inputSchema: e.target.value,
                  });
                }}
                className={cn(
                  "font-mono text-sm min-h-[100px]",
                  inputSchemaError && "border-destructive",
                )}
                rows={6}
              />
              {inputSchemaError ? (
                <p className="text-xs text-destructive break-words">
                  {inputSchemaError}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  JSON Schema defining what input variables the script accepts.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pm-default-inputs">Default Inputs (JSON)</Label>
              <Textarea
                id="pm-default-inputs"
                value={formData.defaultInputs}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defaultInputs: e.target.value,
                  })
                }
                className="font-mono text-sm min-h-[80px]"
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Default values merged with user-provided inputs before
                execution.
              </p>
            </div>

            {mode === "edit" && priceModelId && (
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label>Test Execution</Label>
                  <Badge variant="outline">v{priceModel?.version || 1}</Badge>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pm-test-inputs" className="text-sm">
                    Test Inputs (JSON)
                  </Label>
                  <Textarea
                    id="pm-test-inputs"
                    value={testInputs}
                    onChange={(e) => setTestInputs(e.target.value)}
                    className="font-mono text-sm min-h-[80px]"
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pm-test-quantities" className="text-sm">
                    Quantities (comma-separated)
                  </Label>
                  <Input
                    id="pm-test-quantities"
                    value={testQuantities}
                    onChange={(e) => setTestQuantities(e.target.value)}
                    placeholder="1, 10, 100"
                  />
                </div>

                <Button
                  variant="outline"
                  onClick={handleTestExecute}
                  disabled={isExecuting}
                  className="w-full"
                >
                  {isExecuting ? (
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  Run Test
                </Button>

                {executeError && (
                  <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    {executeError}
                  </div>
                )}

                {testResult && (
                  <div className="space-y-2">
                    <Label className="text-sm">Output</Label>
                    <pre className="rounded-md bg-muted p-3 text-xs font-mono overflow-auto max-h-[300px]">
                      {JSON.stringify(testResult, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <SheetFooter className="pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving && <Loader className="h-4 w-4 mr-2 animate-spin" />}
            {mode === "create" ? "Create" : "Save Changes"}
          </Button>
        </SheetFooter>
      </SheetContent>

      <AlertDialog open={showUpdateConfirm} onOpenChange={setShowUpdateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Price Model</AlertDialogTitle>
            <AlertDialogDescription>
              Saving changes to{" "}
              <span className="font-medium text-foreground">
                {priceModel?.name}
              </span>{" "}
              will recalculate pricing for all items currently assigned to this
              model.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowUpdateConfirm(false);
                performSave();
              }}
              autoFocus
            >
              Update Price Model
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
